/** "Narcotics seizure", "Victim’s advocate", "Grant": a tag that follows the item for life, with where its money goes. */
import { useState } from 'react';
import { FUNDING, type Funding } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { ErrorBox, useAction } from '../shared/ui.tsx';

export function FundingBadge({ funding, refText }: { funding: Funding | null | undefined; refText?: string | null }) {
  if (!funding) return null;
  return (
    <span className={`fund-badge fund-${funding}`} title={FUNDING[funding].proceeds}>
      {FUNDING[funding].label}
      {refText ? ` · ${refText}` : ''}
    </span>
  );
}

/** The tag on a detail page, with "where the money goes"; staff who manage the item can set it. */
export function FundingBox({ kind, id, funding, refText, canEdit, onSaved, sold }: { kind: 'vehicle' | 'equipment' | 'evidence'; id: string; funding: Funding | null; refText: string | null; canEdit: boolean; onSaved: () => void; sold?: boolean }) {
  const { can } = useAuth();
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState<{ funding: string; ref: string; note: string }>({ funding: funding ?? '', ref: refText ?? '', note: '' });
  const a = useAction();
  const locked = !!funding && !can('admin.config');
  return (
    <div className={`fund-box${funding ? ` fund-${funding}` : ''}`}>
      {funding ? (
        <>
          <div>
            <strong>{FUNDING[funding].label}</strong>
            {refText ? <span> · {FUNDING[funding].ref} {refText}</span> : null}
          </div>
          <div className="small">{sold ? '⚠ This was sold. ' : ''}{FUNDING[funding].proceeds}</div>
        </>
      ) : (
        <div className="small muted">Funding: regular agency property.</div>
      )}
      {canEdit && !edit && !locked && (
        <button type="button" className="btn link small" onClick={() => setEdit(true)}>
          {funding ? 'Change' : 'Mark as narcotics seizure, victim’s advocate or grant'}
        </button>
      )}
      {canEdit && locked && <div className="small muted">This tag stays with it for life; only an administrator can change it.</div>}
      {edit && (
        <div className="stack sm" style={{ marginTop: 6 }}>
          <div className="grid-3">
            <label>
              Came from / paid for by
              <select value={f.funding} onChange={(e) => setF({ ...f, funding: e.target.value })}>
                <option value="">Regular agency property</option>
                {(Object.keys(FUNDING) as Funding[]).map((k) => (
                  <option key={k} value={k}>
                    {FUNDING[k].label}
                  </option>
                ))}
              </select>
            </label>
            {f.funding && (
              <label>
                {FUNDING[f.funding as Funding].ref}
                <input value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} />
              </label>
            )}
            {funding && (
              <label>
                Why it’s changing
                <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
              </label>
            )}
          </div>
          <div className="row gap-sm">
            <button type="button" className="btn primary sm" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/funding/${kind}/${id}`, { funding: f.funding || null, ref: f.ref || undefined, note: f.note || undefined }), setEdit(false), onSaved()))}>
              Save
            </button>
            <button type="button" className="btn ghost sm" onClick={() => setEdit(false)}>
              Cancel
            </button>
          </div>
          <ErrorBox error={a.error} />
        </div>
      )}
    </div>
  );
}
